// ═══════════════════════════════════════════════════════════════
// O AJUSTE FUNCIONOU? — recomendação que ninguém mede é palpite
// ═══════════════════════════════════════════════════════════════
//
// A IA lê a conversa e recomenda um ajuste. A pergunta que importa vem depois:
// quem recebeu a recomendação passou a conversar melhor, e isso virou resposta
// do fornecedor?
//
// A comparação é ANTES e DEPOIS da primeira recomendação de cada vendedor —
// porque a recomendação é orientação para a PESSOA, não para o parceiro. E ela
// tem trava de amostra: com quatro conversas de cada lado, qualquer diferença é
// sorte, e número de sorte apresentado como resultado faz a gerência mudar o
// que estava funcionando.

export const MINIMO_POR_LADO = 5;

/** Cada análise já feita, com o que interessa para julgar atendimento. */
export function leituras(partners = []) {
  const saida = [];
  for (const p of partners || []) {
    for (const it of Array.isArray(p.interactions) ? p.interactions : []) {
      const a = it.analiseIa;
      if (!a?.ts) continue;
      saida.push({
        data: String(a.ts).slice(0, 10),
        autor: it.autor || "—",
        empresa: p.name || "",
        parceiroId: p.id,
        // O que a IA leu da conversa
        humana: a.humanizacao === "humana",
        robotica: a.humanizacao === "robotica",
        foco: a.foco && a.foco !== "nenhum" ? a.foco : null,
        sugestao: a.sugestao || "",
        // Fatos conferidos por string, não pelo modelo
        colou: Array.isArray(a.colagem) && a.colagem.length > 0,
        blocao: Number(a.mensagens_longas || 0) > 0,
        // O resultado, que é o que tudo isto está tentando mover
        respondeu: a.houve_resposta === true,
        aceitou: it.resultado === "aceitou",
      });
    }
  }
  return saida.sort((a, b) => (a.data || "").localeCompare(b.data || ""));
}

const pct = (parte, total) => (total ? Math.round((parte / total) * 100) : null);

function fatia(lista) {
  const n = lista.length;
  return {
    n,
    respondeu: pct(lista.filter((l) => l.respondeu).length, n),
    humana: pct(lista.filter((l) => l.humana).length, n),
    colou: pct(lista.filter((l) => l.colou).length, n),
    aceitou: pct(lista.filter((l) => l.aceitou).length, n),
  };
}

/**
 * Antes e depois por vendedor. `lendo` diz se a amostra já permite concluir —
 * quando é false, a tela mostra "ainda juntando conversas" em vez de um número.
 */
export function efeitoPorVendedor(partners = []) {
  const todas = leituras(partners);
  const porAutor = new Map();
  for (const l of todas) {
    if (!porAutor.has(l.autor)) porAutor.set(l.autor, []);
    porAutor.get(l.autor).push(l);
  }

  const saida = [];
  for (const [autor, lista] of porAutor) {
    // A primeira recomendação é o marco. Antes dela a pessoa trabalhava sem
    // nenhum retorno da IA; depois, com.
    const primeira = lista.find((l) => l.foco);
    if (!primeira) continue;
    const antes = fatia(lista.filter((l) => l.data < primeira.data));
    const depois = fatia(lista.filter((l) => l.data >= primeira.data));
    const lendo = antes.n >= MINIMO_POR_LADO && depois.n >= MINIMO_POR_LADO;
    saida.push({
      autor,
      desde: primeira.data,
      antes,
      depois,
      lendo,
      // Só existe quando dá para ler. Sem amostra não se calcula delta: um
      // campo preenchido convida a gerência a interpretá-lo.
      delta: lendo && antes.respondeu != null && depois.respondeu != null
        ? depois.respondeu - antes.respondeu
        : null,
      focos: contarFocos(lista),
    });
  }
  return saida.sort((a, b) => b.depois.n - a.depois.n);
}

function contarFocos(lista) {
  const m = new Map();
  for (const l of lista) {
    if (!l.foco) continue;
    if (!m.has(l.foco)) m.set(l.foco, { foco: l.foco, total: 0, ultima: "" });
    const f = m.get(l.foco);
    f.total += 1;
    if (l.data > f.ultima) f.ultima = l.data;
  }
  return [...m.values()].sort((a, b) => b.total - a.total);
}

/**
 * Quais ajustes mais se repetem no time. Foco que aparece muito é assunto de
 * treinamento, não de recado individual — e é a leitura mais acionável que
 * este painel produz mesmo com pouca amostra.
 */
export function focosDoTime(partners = []) {
  const lista = leituras(partners);
  const m = new Map();
  for (const l of lista) {
    if (!l.foco) continue;
    if (!m.has(l.foco)) m.set(l.foco, { foco: l.foco, total: 0, pessoas: new Set(), ultima: "" });
    const f = m.get(l.foco);
    f.total += 1;
    f.pessoas.add(l.autor);
    if (l.data > f.ultima) f.ultima = l.data;
  }
  return [...m.values()]
    .map((f) => ({ ...f, pessoas: f.pessoas.size }))
    .sort((a, b) => b.total - a.total);
}

export const ROTULO_FOCO = {
  abertura: "abertura da conversa",
  tamanho: "mensagem grande demais",
  escuta: "não respondeu o que o fornecedor perguntou",
  proximo_passo: "conversa sem próximo passo combinado",
  objecao: "objeção que ficou sem resposta",
  conteudo: "faltou explicar o essencial",
  colagem: "texto padrão colado",
};

/** O quadro geral, para o topo do painel. */
export function resumoEfeito(partners = []) {
  const lista = leituras(partners);
  const porVendedor = efeitoPorVendedor(partners);
  const comLeitura = porVendedor.filter((v) => v.lendo);
  return {
    analisadas: lista.length,
    comRecomendacao: lista.filter((l) => l.foco).length,
    robotica: lista.filter((l) => l.robotica).length,
    colou: lista.filter((l) => l.colou).length,
    pessoas: porVendedor.length,
    comLeitura: comLeitura.length,
    melhorou: comLeitura.filter((v) => (v.delta || 0) > 0).length,
    piorou: comLeitura.filter((v) => (v.delta || 0) < 0).length,
    focos: focosDoTime(partners),
  };
}
